/**
 * Google OAuth 2.0 implicit flow (id_token) — same-tab redirect.
 *
 * Why not GIS popup:
 *   iOS Safari (and Chrome on iOS) routinely converts the GSI popup window
 *   to a new browser tab. After successful sign-in, the user lands on
 *   accounts.google.com and has to switch back to the original tab to see
 *   the logged-in state. Redirect flow avoids this — the page navigates to
 *   Google in-place, then back to `/oauth/callback` with `id_token` in the
 *   URL hash. SPA reads it, posts to our Worker, navigates to /dashboard.
 *
 * Why implicit (response_type=id_token) and not Authorization Code + PKCE:
 *   Our Worker accepts the Google id_token directly (it verifies via JWKS
 *   at `accounts.google.com`). Code flow would require exchanging the code
 *   for a token via a server endpoint — extra hop with no security gain in
 *   this flow because the Worker is the only party that ever sees the token.
 *
 * Nonce: stored in sessionStorage before redirect; verified after redirect
 * by comparing against the token's `nonce` claim — replay-protection per
 * OpenID Connect §3.2.2.10.
 */

const NONCE_STORAGE_KEY = "isale.oauth.nonce";
const RETURN_TO_KEY = "isale.oauth.returnTo";
const PROVIDER_KEY = "isale.oauth.provider";
const CALLBACK_PATH = "/oauth/callback";

/**
 * FL-2: Sanitize a post-sign-in return path.
 *
 * Only accepts internal paths that begin with "/" but NOT "//".
 * Blocks open-redirect via "//evil.com" or "http://evil.com".
 * Exported so it can be tested independently.
 */
export function sanitizeReturnTo(path: string): string {
  if (typeof path === "string" && path.startsWith("/") && !path.startsWith("//")) {
    return path;
  }
  return "/dashboard";
}

/**
 * FL-2: Persist the intended post-sign-in destination so the auth guard
 * can save the current URL before redirecting to /sign-in. Called by
 * Layout.tsx when the session check fails.
 */
export function storeReturnTo(path: string): void {
  sessionStorage.setItem(RETURN_TO_KEY, sanitizeReturnTo(path));
}

/**
 * FL-2: Read the stored return-to path written by the auth guard. Returns
 * the sanitized stored value, or "/dashboard" if nothing was stored.
 * Does NOT clear the value — buildGoogleAuthorizeUrl / buildAppleAuthorizeUrl
 * will overwrite it (and parseCallbackFromHash clears it on callback).
 */
export function readStoredReturnTo(): string {
  return sanitizeReturnTo(sessionStorage.getItem(RETURN_TO_KEY) ?? "/dashboard");
}

export type OAuthProvider = "google" | "apple";

function randomNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export interface BuildAuthorizeArgs {
  clientId: string;
  /** Optional: where to navigate after successful sign-in. Defaults to `/dashboard`. */
  returnTo?: string;
}

export function buildGoogleAuthorizeUrl(args: BuildAuthorizeArgs): string {
  const nonce = randomNonce();
  sessionStorage.setItem(NONCE_STORAGE_KEY, nonce);
  sessionStorage.setItem(PROVIDER_KEY, "google");
  if (args.returnTo) sessionStorage.setItem(RETURN_TO_KEY, args.returnTo);
  const redirectUri = `${window.location.origin}${CALLBACK_PATH}`;
  const params = new URLSearchParams({
    response_type: "id_token",
    client_id: args.clientId,
    redirect_uri: redirectUri,
    scope: "openid email profile",
    nonce,
    // `select_account consent` forces Google to ALWAYS show the account
    // chooser (even when only one account is signed in to the browser
    // session) AND the consent screen. Plain `select_account` will silently
    // auto-advance with the lone active account if Chrome only has one
    // Google session — Vincent's "auto-picks default account" feedback
    // (2026-05-20). Tradeoff: an extra "Allow" click on every sign-in.
    prompt: "select_account consent",
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

/**
 * Apple Sign In with Apple — web flow (Service ID, response_mode=fragment).
 *
 * Why fragment + id_token (mirrors Google):
 *   Apple supports both `form_post` (default, POSTs to redirect_uri) and
 *   `fragment` response modes. We use `fragment` so the OAuthCallback page
 *   can read id_token from `window.location.hash` exactly like Google —
 *   single code path for both providers.
 *
 * Apple requires `response_type=code id_token` (not just id_token like Google);
 * we throw away the `code` since the Worker accepts the id_token directly
 * via JWKS verification at `https://appleid.apple.com/auth/keys`.
 *
 * Service ID + return URL must be whitelisted in Apple Developer Console:
 *   - APPLE_OAUTH_CLIENT_ID env var = Service ID (e.g. `app.isale.web.dev`)
 *   - Return URL `https://isale-web-dev.pages.dev/oauth/callback` added
 *     to "Domains and Subdomains" + "Return URLs" of the Service ID
 *   - Apple Developer Console > Identifiers > Service IDs > Configure
 *
 * Apple-specific quirk: `usePopup: false` is implicit when using redirect
 * URL (we're not using AppleID JS SDK at all, just URL params).
 */
export function buildAppleAuthorizeUrl(args: BuildAuthorizeArgs): string {
  const nonce = randomNonce();
  sessionStorage.setItem(NONCE_STORAGE_KEY, nonce);
  sessionStorage.setItem(PROVIDER_KEY, "apple");
  if (args.returnTo) sessionStorage.setItem(RETURN_TO_KEY, args.returnTo);
  const redirectUri = `${window.location.origin}${CALLBACK_PATH}`;
  const params = new URLSearchParams({
    response_type: "code id_token",
    response_mode: "fragment",
    client_id: args.clientId,
    redirect_uri: redirectUri,
    scope: "email name",
    state: nonce,
    nonce,
  });
  return `https://appleid.apple.com/auth/authorize?${params.toString()}`;
}

export interface CallbackResult {
  idToken: string;
  returnTo: string;
  /** Which provider initiated this flow — read from sessionStorage. Drives
   *  which `/v1/auth/{provider}/login` endpoint the callback POSTs to. */
  provider: OAuthProvider;
  /** Set when the callback URL contains an error (user cancelled, scope denied, …). */
  error?: string;
}

/**
 * Parses the OAuth callback URL fragment. Returns either a valid `idToken`
 * + `returnTo`, or sets `error` describing why we couldn't continue.
 */
export function parseCallbackFromHash(hash: string): CallbackResult | null {
  // Strip leading "#" if present.
  const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!fragment) return null;
  const params = new URLSearchParams(fragment);
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY) ?? "/dashboard";
  const provider =
    (sessionStorage.getItem(PROVIDER_KEY) as OAuthProvider | null) ?? "google";
  // Always clear nonce + returnTo + provider so a back-button replay can't be reused.
  const expectedNonce = sessionStorage.getItem(NONCE_STORAGE_KEY);
  sessionStorage.removeItem(NONCE_STORAGE_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  sessionStorage.removeItem(PROVIDER_KEY);

  const error = params.get("error");
  if (error) return { idToken: "", returnTo, provider, error };
  const idToken = params.get("id_token");
  if (!idToken) return null;

  // Verify nonce echoed by IdP matches what we sent. Both Google + Apple
  // echo the nonce in the id_token's `nonce` claim per OIDC §3.2.2.10.
  const payload = decodeJwtSegment(idToken.split(".")[1]);
  if (!payload || payload.nonce !== expectedNonce) {
    return { idToken: "", returnTo, provider, error: "nonce_mismatch" };
  }
  return { idToken, returnTo, provider };
}

function decodeJwtSegment(seg: string | undefined): Record<string, unknown> | null {
  if (!seg) return null;
  try {
    const b64 = seg.replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
    return JSON.parse(atob(b64 + pad)) as Record<string, unknown>;
  } catch {
    return null;
  }
}
